import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2 } from "lucide-react";

interface SubscriptionEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading?: boolean;
  plan: "monthly" | "annual";
  onSubmit: (email: string) => void;
}

const SubscriptionEmailDialog = ({ open, onOpenChange, loading, plan, onSubmit }: SubscriptionEmailDialogProps) => {
  const { t } = useTranslation("landing");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(email)) {
      setError(t("pricing.email_invalid", "Please enter a valid email address."));
      return;
    }
    setError("");
    onSubmit(email.trim().toLowerCase());
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !loading && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#fcb424]" />
            {t("pricing.email_gate_title", "Enter your email to continue")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "pricing.email_gate_desc",
              "We'll create your account with this email and send you a temporary password after payment."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <Label htmlFor="sub-email">{t("pricing.email_label", "Email address")}</Label>
          <Input
            id="sub-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            disabled={loading}
            autoFocus
          />
          {error && <p className="text-xs text-destructive">{error}</p>}

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-[#fcb424] text-[#070e1e] hover:bg-[#fcb424]/90 font-semibold"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("pricing.email_gate_loading", "Starting checkout…")}
              </>
            ) : (
              t("pricing.email_gate_cta", plan === "annual" ? "Continue to Annual checkout" : "Continue to Monthly checkout")
            )}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">
            {t(
              "pricing.email_gate_note",
              "Already have an account? Use the same email — we'll just activate your subscription."
            )}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionEmailDialog;
